import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface OrderItem {
  id: number;
  product: { id: number; name: string; sku: string };
  quantity: number;
  original_quantity: number | null;
  unit_price: number;
  subtotal: number;
}

export interface CompanyOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_id: string;
  status: string;
  order_date: string;
  delivery_date: string | null;
  delivered_at: string | null;
  total_amount: number;
  original_total_amount: number | null;
  notes: string;
  payment_status: string;
  payment_link_url: string;
  items: OrderItem[];
}

export interface CustomerOption {
  id: number;
  customer_id: string;
  company_name: string;
}

export const statusColors: Record<string, string> = {
  pending: 'warning',
  confirmed: 'primary',
  processing: 'info',
  shipped: 'secondary',
  delivered: 'success',
  cancelled: 'danger',
};

export function useCompanyOrders() {
  const { isExpired } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState<CompanyOrder[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: searchParams.get('status') || '', customer: searchParams.get('customer') || '' });
  const [selectedOrder, setSelectedOrder] = useState<CompanyOrder | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [deliveryEdit, setDeliveryEdit] = useState<{ orderId: number | null; tempDate: string }>({ orderId: null, tempDate: '' });
  const [editState, setEditState] = useState<{ mode: number | null; quantities: Record<number, number>; saving: boolean }>({ mode: null, quantities: {}, saving: false });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.status) params.status = filters.status;
      if (filters.customer) params.customer_id = filters.customer;
      const response = await companyAPI.getAllOrders(params);
      setOrders(response.data);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.customer]);

  const fetchCustomers = async () => {
    try {
      const response = await companyAPI.getCustomers();
      setCustomers(response.data.map((c: any) => ({
        id: c.id,
        customer_id: c.customer_id,
        company_name: c.company_name,
      })));
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);
  useEffect(() => { fetchOrders(); }, [filters.status, filters.customer, fetchOrders]);

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    setUpdatingStatus(orderId);
    try {
      const response = await companyAPI.updateOrderStatus(orderId, newStatus);
      const updatedDate = response.data.delivered_at || null;
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus, delivered_at: updatedDate } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus, delivered_at: updatedDate });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeliveryDateSave = async (orderId: number) => {
    const dateToSave = deliveryEdit.tempDate || null;
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, delivered_at: dateToSave } : o));
    setSelectedOrder(prev => prev && prev.id === orderId ? { ...prev, delivered_at: dateToSave } : prev);
    setDeliveryEdit({ orderId: null, tempDate: '' });
    try {
      await companyAPI.updateOrderDeliveryDate(orderId, dateToSave);
    } catch (err) {
      console.error('Failed to update delivery date:', err);
      fetchOrders();
    }
  };

  const enterEditMode = () => {
    if (!selectedOrder) return;
    const qtyMap: Record<number, number> = {};
    selectedOrder.items.forEach(item => { qtyMap[item.id] = item.quantity; });
    setEditState({ mode: selectedOrder.id, quantities: qtyMap, saving: false });
  };

  const cancelEdit = () => {
    setEditState({ mode: null, quantities: {}, saving: false });
  };

  const handleSaveEdit = async () => {
    if (!selectedOrder) return;
    setEditState(prev => ({ ...prev, saving: true }));
    try {
      const items = Object.entries(editState.quantities).map(([id, quantity]) => ({
        id: Number(id),
        quantity,
      }));
      const response = await companyAPI.editOrderItems(selectedOrder.id, items);
      const updated = response.data;
      setSelectedOrder(prev => prev ? {
        ...prev,
        total_amount: updated.total_amount,
        original_total_amount: updated.original_total_amount,
        items: updated.items,
      } : prev);
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? {
        ...o,
        total_amount: updated.total_amount,
        original_total_amount: updated.original_total_amount,
        items: updated.items,
      } : o));
      setEditState({ mode: null, quantities: {}, saving: false });
    } catch (err) {
      console.error('Failed to save order edits:', err);
    } finally {
      setEditState(prev => ({ ...prev, saving: false }));
    }
  };

  const editedTotal = selectedOrder
    ? selectedOrder.items.reduce((sum, item) => {
        const qty = editState.quantities[item.id] ?? item.quantity;
        return sum + item.unit_price * qty;
      }, 0)
    : 0;

  const formatDate = (dateStr: string) => {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const clearFilters = () => {
    setFilters({ status: '', customer: '' });
    setSearchParams({});
  };

  const selectedCustomer = filters.customer ? customers.find(c => String(c.id) === String(filters.customer)) : null;

  const customerStats = useMemo(() => {
    if (!filters.customer || orders.length === 0) return null;
    const nonCancelled = orders.filter(o => o.status !== 'cancelled');
    const totalAmount = nonCancelled.reduce((sum, o) => sum + o.total_amount, 0);
    const avgAmount = nonCancelled.length > 0 ? totalAmount / nonCancelled.length : 0;
    const delivered = orders.filter(o => o.status === 'delivered').length;
    const cancelled = orders.filter(o => o.status === 'cancelled').length;
    const pending = orders.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)).length;
    const allAmounts = nonCancelled.map(o => o.total_amount);
    const largest = allAmounts.length > 0 ? Math.max(...allAmounts) : 0;
    const smallest = allAmounts.length > 0 ? Math.min(...allAmounts) : 0;

    const dates = orders.map(o => new Date(o.order_date)).sort((a, b) => a.getTime() - b.getTime());
    const firstOrder = dates[0];
    const lastOrder = dates[dates.length - 1];

    return { totalAmount, avgAmount, delivered, cancelled, pending, largest, smallest, firstOrder, lastOrder, totalOrders: orders.length, activeOrders: nonCancelled.length };
  }, [orders, filters.customer]);

  const closeDetailModal = () => {
    setSelectedOrder(null);
    setEditState({ mode: null, quantities: {}, saving: false });
  };

  const closeDetailModalSimple = () => {
    setSelectedOrder(null);
    setEditState(prev => ({ ...prev, mode: null }));
  };

  const cancelDeliveryEdit = () => {
    setDeliveryEdit(prev => ({ ...prev, orderId: null }));
  };

  return {
    // Auth
    isExpired,

    // State
    orders,
    customers,
    loading,
    filters,
    setFilters,
    selectedOrder,
    setSelectedOrder,
    updatingStatus,
    deliveryEdit,
    setDeliveryEdit,
    editState,
    setEditState,

    // Computed
    editedTotal,
    selectedCustomer,
    customerStats,

    // Handlers
    handleStatusChange,
    handleDeliveryDateSave,
    enterEditMode,
    cancelEdit,
    handleSaveEdit,
    formatDate,
    clearFilters,
    closeDetailModal,
    closeDetailModalSimple,
    cancelDeliveryEdit,
  };
}
